import { useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { ArrowLeft, BookOpen, Printer } from 'lucide-react'

// Estrutura comum dos manuais de uso do sistema: cabeçalho com voltar e imprimir, índice lateral e o corpo do texto.
// Cada manual passa só o conteúdo (h2 com id igual ao do índice) e usa Pill, Botao, Dica, Atencao e Passo.

const css = `
.mn { display: grid; grid-template-columns: 230px minmax(0, 1fr); gap: 24px; align-items: start; max-width: 1180px; color: #1f2d3d }
.mn-indice { position: sticky; top: 12px; background: #fff; border: 1px solid #e2e8f0; border-radius: 12px; padding: 12px; display: flex; flex-direction: column; gap: 2px }
.mn-indice b { font-size: 11px; letter-spacing: .06em; color: #688095; padding: 2px 8px 6px }
.mn-indice button { border: 0; background: none; text-align: left; padding: 6px 8px; border-radius: 7px; font-size: 12.5px; color: #1a6ea8; font-weight: 600; cursor: pointer }
.mn-indice button:hover { background: #eef5fa }
.mn-corpo { background: #fff; border: 1px solid #e2e8f0; border-radius: 12px; padding: 26px 30px 34px; font-size: 14px; line-height: 1.6; min-width: 0 }
.mn-corpo h2 { margin: 30px 0 10px; font-size: 18px; color: #123f68; padding-top: 18px; border-top: 1px solid #e8eef4; scroll-margin-top: 12px }
.mn-corpo h2:first-of-type { border-top: 0; margin-top: 8px }
.mn-corpo h3 { margin: 18px 0 6px; font-size: 14.5px; color: #1b3f7b }
.mn-corpo p { margin: 6px 0 }
.mn-corpo ul, .mn-corpo ol { margin: 6px 0 6px 20px; padding: 0 }
.mn-corpo li { margin: 4px 0 }
.mn-caixa { border-radius: 10px; padding: 11px 14px; margin: 12px 0; font-size: 13.5px }
.mn-dica { background: #eef6fc; border-left: 4px solid #1a6ea8 }
.mn-atencao { background: #fff7ec; border-left: 4px solid #f28b20 }
.mn-futuro { background: #f5f3ff; border-left: 4px solid #7c3aed }
.mn-exemplo { background: #f6f9fb; border: 1px dashed #b7c9d8 }
.mn-formula { background: #123f68; color: #fff; border-radius: 10px; padding: 10px 14px; margin: 10px 0; font-weight: 700; font-size: 14px }
.mn-tabela { width: 100%; border-collapse: collapse; margin: 10px 0; font-size: 13px }
.mn-tabela th { background: #1b3f7b; color: #fff; text-align: left; padding: 7px 10px; font-size: 11.5px; text-transform: uppercase }
.mn-tabela td { padding: 7px 10px; border-bottom: 1px solid #e8eef4; vertical-align: top }
.mn-pill { display: inline-block; padding: 1px 9px; border-radius: 999px; font-size: 12px; font-weight: 800; white-space: nowrap }
.mn-botao { display: inline-block; padding: 1px 9px; border-radius: 6px; font-size: 12.5px; font-weight: 700; background: #eef2f6; border: 1px solid #cbd5e1; color: #1e293b; white-space: nowrap }
.mn-passo { display: grid; grid-template-columns: 30px 1fr; gap: 10px; margin: 10px 0 }
.mn-passo > span { width: 28px; height: 28px; border-radius: 50%; background: #1b3f7b; color: #fff; font-weight: 800; display: flex; align-items: center; justify-content: center; font-size: 13px }
@media (max-width: 900px) { .mn { grid-template-columns: 1fr } .mn-indice { position: static } }
@media print {
  .sidebar, .topbar, .mn-indice, .mn-nao-imprimir { display: none !important }
  .mn { display: block } .mn-corpo { border: 0; padding: 0 } .mn-corpo h2 { break-after: avoid } .mn-caixa, .mn-passo, .mn-tabela tr { break-inside: avoid }
}
`

// Botão "Manual" das telas: bem visível, com ícone de livro; abre o manual (com âncora opcional na rota, ex.: ...#analisar)
export function BotaoManual({ para, titulo = 'Abrir o manual desta tela', altura }: { para: string; titulo?: string; altura?: number }) {
  const navigate = useNavigate()
  return (
    <button type="button" onClick={() => navigate(para)} title={titulo}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 6, height: altura, padding: '0.35rem 0.9rem', borderRadius: 8, cursor: 'pointer',
        border: '1.5px solid #1a6ea8', background: '#eaf3fb', color: '#14547f', fontWeight: 700, fontSize: '0.82rem', whiteSpace: 'nowrap',
        boxShadow: '0 1px 2px rgba(20,84,127,0.15)' }}>
      <BookOpen size={15} /> Manual
    </button>
  )
}

export const Pill =({ t, cor, fundo }: { t: string; cor: string; fundo: string }) => <span className="mn-pill" style={{ color: cor, background: fundo }}>{t}</span>
export const Botao = ({ t }: { t: string }) => <span className="mn-botao">{t}</span>
export const Dica = ({ children }: { children: React.ReactNode }) => <div className="mn-caixa mn-dica">💡 {children}</div>
export const Atencao = ({ children }: { children: React.ReactNode }) => <div className="mn-caixa mn-atencao">⚠️ {children}</div>
export const Futuro = ({ children }: { children: React.ReactNode }) => <div className="mn-caixa mn-futuro">🔮 {children}</div>
export const Passo = ({ n, titulo, children }: { n: number; titulo: string; children?: React.ReactNode }) => (
  <div className="mn-passo"><span>{n}</span><div><b>{titulo}</b>{children}</div></div>
)

export default function ManualPagina({ titulo, subtitulo, voltarTexto, voltarPara, secoes, children }:
  { titulo: string; subtitulo: string; voltarTexto: string; voltarPara: string; secoes: { id: string; rot: string }[]; children: React.ReactNode }) {
  const navigate = useNavigate()
  const { hash } = useLocation()
  const ir = (id: string) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  useEffect(() => { if (hash) setTimeout(() => ir(hash.slice(1)), 50) }, [hash])

  return (
    <div style={{ width: '100%' }}>
      <style>{css}</style>
      <div className="mn-nao-imprimir" style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 14, maxWidth: 1180 }}>
        <button className="btn btn-outline btn-sm" onClick={() => navigate(voltarPara)} style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><ArrowLeft size={14} /> {voltarTexto}</button>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ width: 32, height: 32, borderRadius: 8, background: '#e5f1f7', color: '#126f9f', display: 'grid', placeItems: 'center' }}><BookOpen size={17} /></span>
          <div>
            <div style={{ fontWeight: 800, fontSize: 17, color: '#123f68' }}>{titulo}</div>
            <div style={{ fontSize: 12, color: '#64748b' }}>{subtitulo}</div>
          </div>
        </div>
        <button className="btn btn-outline btn-sm" onClick={() => window.print()} style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 5 }}><Printer size={14} /> Imprimir / salvar em PDF</button>
      </div>

      <div className="mn">
        <nav className="mn-indice">
          <b>NESTE MANUAL</b>
          {secoes.map(s => <button key={s.id} type="button" onClick={() => ir(s.id)}>{s.rot}</button>)}
        </nav>
        <article className="mn-corpo">{children}</article>
      </div>
    </div>
  )
}
